/**
 * My Account → Documents (CLIENT_DOCUMENTS_PLAN.md): documents the client's account manager sent from the
 * CRM, and signing them. Being signed in is the identity check; a staff session opened from the CRM can
 * read but not sign. The document's HTML is rendered by walking a parsed tree into React elements with the
 * same tag allowlist the API applies, never as raw HTML.
 */
import { createElement, Fragment, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import {
  DOCUMENT_CONSENT_TEXT,
  DOCUMENT_HTML_DROP,
  DOCUMENT_HTML_TAGS,
  DOCUMENT_LIMITS,
  documentSignable,
  fmtDate,
  type ClientDocument,
  type ClientDocumentStatus,
  type ClientDocumentSummary,
  type SignatureMethod,
} from '@platform/shared';
import { cx, Icon, Spinner } from '../../components/ui';
import { api, downloadFile, staffSession } from '../../lib/api';
import { Link } from '../../lib/router';
import { useLoad } from '../../stores/account';
import { loadDocuments, useDocuments } from '../../stores/documents';
import { useMe } from '../../stores/session';
import { AIcon, Card, Chip, errMsg, H1, H2, List, PillButton, PillLink, Segmented } from './kit';

// ---------- the document's text ----------

const ALLOWED = new Set<string>(DOCUMENT_HTML_TAGS);
const DROP = new Set<string>(DOCUMENT_HTML_DROP);

function walk(node: Node, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const tag = (node as Element).tagName.toLowerCase();
  if (DROP.has(tag)) return null;
  const kids = Array.from(node.childNodes, (c, i) => walk(c, `${key}.${i}`));
  if (!ALLOWED.has(tag)) return createElement(Fragment, { key }, ...kids);
  if (tag === 'br' || tag === 'hr') return createElement(tag, { key });
  return createElement(tag, { key }, ...kids);
}

/** The document's HTML as React elements: allowed tags only, no attributes, the text of everything else. */
export function DocumentHtml({ html }: { html: string }) {
  const nodes = useMemo(() => {
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    return Array.from(body.childNodes, (c, i) => walk(c, String(i)));
  }, [html]);
  return <div className="doc-paper-body">{nodes}</div>;
}

// ---------- labels ----------

const KIND_LABEL = { agreement: 'Agreement', letter: 'Letter', report: 'Report' } as const;

function statusChip(d: Pick<ClientDocumentSummary, 'status' | 'requiresSignature'>) {
  const map: Record<ClientDocumentStatus, [string, 'up' | 'down' | 'warn' | 'accent' | 'muted']> = {
    sent: d.requiresSignature ? ['To sign', 'warn'] : ['New', 'accent'],
    viewed: d.requiresSignature ? ['To sign', 'warn'] : ['Read', 'muted'],
    signed: ['Signed', 'up'],
    declined: ['Declined', 'down'],
    expired: ['Expired', 'muted'],
    voided: ['Withdrawn', 'muted'],
  };
  const [label, tone] = map[d.status];
  return <Chip tone={tone}>{label}</Chip>;
}

const pdfName = (reference: string) => `${reference.replace(/[^A-Za-z0-9._-]+/g, '_') || 'document'}.pdf`;

function DownloadButton({ doc, kind = 'secondary' }: { doc: Pick<ClientDocument, 'id' | 'reference'>; kind?: 'primary' | 'secondary' }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-1.5">
      <PillButton
        kind={kind}
        size="sm"
        busy={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            await downloadFile(`/api/documents/${doc.id}/file`, pdfName(doc.reference));
          } catch (e) {
            setErr(errMsg(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        <AIcon name="down" size={16} /> Download PDF
      </PillButton>
      {err && <span className="text-[13px] text-down">{err}</span>}
    </div>
  );
}

// ---------- the list ----------

export function DocumentsPage() {
  const list = useDocuments();
  useEffect(() => {
    void loadDocuments();
  }, []);
  return (
    <div className="flex flex-col gap-8">
      <H1>Documents</H1>
      <Card>
        {!list ? (
          <div className="grid place-items-center py-8 text-muted">
            <Spinner />
          </div>
        ) : (
          <List empty="No documents yet. Documents from your account manager appear here.">
            {list.data.map((d) => (
              <Link key={d.id} to={`/account/documents/${d.id}`} className="block text-fg no-underline">
                <div className="flex items-center gap-4 border-b border-line py-4 last:border-b-0 md:py-5">
                  <span className={cx('grid size-11 shrink-0 place-items-center rounded-full', documentSignable(d) ? 'bg-warn-bg text-warn' : 'bg-track text-muted')}>
                    <AIcon name="doc" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[16px] font-medium md:text-[17px]">{d.title}</div>
                    <div className="truncate text-[14px] text-muted">
                      {d.reference} · from {d.agentName} · {fmtDate(d.createdAt)}
                      {documentSignable(d) && d.expiresAt ? ` · sign by ${fmtDate(d.expiresAt)}` : ''}
                    </div>
                  </div>
                  {statusChip(d)}
                  <AIcon name="right" size={18} className="shrink-0 text-muted" />
                </div>
              </Link>
            ))}
          </List>
        )}
      </Card>
    </div>
  );
}

/** Dashboard: the document waiting for the client's signature (the newest), until it is signed. */
export function PendingDocumentCard() {
  const list = useDocuments();
  const waiting = (list?.data ?? []).filter((d) => documentSignable(d));
  const d = waiting[0];
  if (!d) return null;
  return (
    <section className="flex flex-wrap items-center gap-x-5 gap-y-4 rounded-[24px] border border-line bg-card p-5 md:px-8 md:py-6">
      <span className="grid size-12 shrink-0 place-items-center rounded-full bg-warn-bg text-warn">
        <AIcon name="doc" size={22} />
      </span>
      <div className="min-w-[12rem] flex-1">
        <div className="text-[16px] font-semibold md:text-[17px]">Please sign: {d.title}</div>
        <div className="text-[14px] text-muted md:text-[15px]">
          from {d.agentName}
          {d.expiresAt ? ` · expires ${fmtDate(d.expiresAt)}` : ''}
          {waiting.length > 1 && (
            <>
              {' · '}
              <Link to="/account/documents" className="text-fg underline underline-offset-4">
                {waiting.length - 1} more to sign
              </Link>
            </>
          )}
        </div>
      </div>
      <PillLink to={`/account/documents/${d.id}`} size="sm">
        Read and sign
      </PillLink>
    </section>
  );
}

// ---------- one document ----------

function Paper({ doc }: { doc: ClientDocument }) {
  return (
    <article className="doc-paper overflow-hidden rounded-[8px] bg-white text-[#16181d] shadow-[0_1px_3px_rgba(0,0,0,0.12),0_8px_30px_rgba(0,0,0,0.08)] ring-1 ring-black/5">
      <header className="px-6 pt-7 md:px-12 md:pt-10">
        {doc.brand.logoDataUrl ? (
          <img src={doc.brand.logoDataUrl} alt={doc.brand.name} className="block max-h-11 max-w-[220px] object-contain" />
        ) : (
          <div className="text-[20px] font-semibold tracking-tight">{doc.brand.name}</div>
        )}
      </header>
      <div className="mx-6 mt-5 h-[3px] md:mx-12" style={{ background: doc.brand.primaryColor }} />
      <div className="px-6 pt-6 pb-10 md:px-12 md:pb-14">
        <div className="text-[12px] font-semibold tracking-[0.08em] uppercase" style={{ color: doc.brand.accentColor }}>
          {KIND_LABEL[doc.kind]} · {doc.reference}
        </div>
        <h1 className="mt-2 mb-6 text-[24px] leading-tight font-semibold md:text-[28px]">{doc.title}</h1>
        <DocumentHtml html={doc.html} />
      </div>
    </article>
  );
}

/** A signature pad: pointer events (mouse, pen, finger), sharp on high-density screens. */
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const ink = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  const setup = () => {
    const c = canvas.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(c.clientWidth * dpr);
    c.height = Math.round(c.clientHeight * dpr);
    const ctx = c.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#16181d';
    ink.current = false;
    onChange(null);
  };

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    let width = c.clientWidth;
    setup();
    // A new width (rotation, a resized window) starts the pad again: the old strokes would be stretched.
    const ro = new ResizeObserver(() => {
      if (c.clientWidth !== width) {
        width = c.clientWidth;
        setup();
      }
    });
    ro.observe(c);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  /** The drawing as a PNG, at most 900 px wide so it stays well under the size limit. */
  const exportPng = () => {
    const c = canvas.current!;
    const scale = Math.min(1, 900 / c.width);
    if (scale === 1) return c.toDataURL('image/png');
    const out = document.createElement('canvas');
    out.width = Math.round(c.width * scale);
    out.height = Math.round(c.height * scale);
    out.getContext('2d')!.drawImage(c, 0, 0, out.width, out.height);
    return out.toDataURL('image/png');
  };

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvas}
        aria-label="Signature pad: draw your signature"
        className="h-[160px] w-full cursor-crosshair touch-none rounded-[16px] border border-line bg-white"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          const p = point(e);
          last.current = p;
          const ctx = e.currentTarget.getContext('2d')!;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2);
          ctx.fillStyle = '#16181d';
          ctx.fill();
          ink.current = true;
        }}
        onPointerMove={(e) => {
          if (!drawing.current || !last.current) return;
          const p = point(e);
          const ctx = e.currentTarget.getContext('2d')!;
          ctx.beginPath();
          ctx.moveTo(last.current.x, last.current.y);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          last.current = p;
        }}
        onPointerUp={() => {
          drawing.current = false;
          last.current = null;
          if (ink.current) onChange(exportPng());
        }}
        onPointerCancel={() => {
          drawing.current = false;
          last.current = null;
        }}
      />
      <div className="flex items-center justify-between text-[13px] text-muted">
        <span>Sign inside the box</span>
        <button type="button" onClick={setup} className="font-semibold text-fg underline underline-offset-4">
          Clear
        </button>
      </div>
    </div>
  );
}

function SignPanel({ doc, onDone }: { doc: ClientDocument; onDone: (d: ClientDocument) => void }) {
  const me = useMe()!;
  const staff = !!staffSession();
  const [name, setName] = useState([me.firstName, me.lastName].filter(Boolean).join(' '));
  const [method, setMethod] = useState<SignatureMethod>('drawn');
  const [image, setImage] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const trimmed = name.trim();
  const nameOk = trimmed.length >= DOCUMENT_LIMITS.signerNameMin && trimmed.length <= DOCUMENT_LIMITS.signerNameMax;
  const canSign = !staff && nameOk && consent && (method === 'typed' || !!image);

  const sign = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await api.post<ClientDocument>(`/api/documents/${doc.id}/sign`, {
        name: trimmed,
        method,
        ...(method === 'drawn' ? { image } : {}),
        consent: true,
        sha256: doc.sha256,
      });
      void loadDocuments();
      onDone(d);
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    setErr(null);
    try {
      const d = await api.post<ClientDocument>(`/api/documents/${doc.id}/decline`, { reason: reason.trim() });
      void loadDocuments();
      onDone(d);
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Card className="flex flex-col gap-5">
      <H2 className="!text-[20px] md:!text-[22px]">Sign this document</H2>
      {staff && <p className="m-0 rounded-[16px] bg-track px-4 py-3 text-[14px] text-muted">Only the client can sign this.</p>}
      <div className="flex flex-col gap-2.5">
        <label htmlFor="doc-signer" className="text-[14px] font-semibold">
          Full name
        </label>
        <input
          id="doc-signer"
          value={name}
          maxLength={DOCUMENT_LIMITS.signerNameMax}
          autoComplete="name"
          onChange={(e) => setName(e.target.value)}
          className="h-[52px] rounded-full border border-line bg-card px-5 text-[16px] text-fg outline-none focus:border-line-strong"
        />
      </div>
      <Segmented
        label="How to sign"
        value={method}
        onChange={setMethod}
        options={[
          { id: 'drawn', label: 'Draw' },
          { id: 'typed', label: 'Type' },
        ]}
      />
      {method === 'drawn' ? (
        <SignaturePad onChange={setImage} />
      ) : (
        <div className="grid h-[160px] place-items-center overflow-hidden rounded-[16px] border border-line bg-white px-4 text-[#16181d]">
          <span className="truncate font-serif text-[40px] italic md:text-[44px]">{trimmed || 'Your name'}</span>
        </div>
      )}
      <label className="flex cursor-pointer items-start gap-3 text-[15px] leading-snug">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 size-5 shrink-0 accent-[var(--text)]" />
        <span>{DOCUMENT_CONSENT_TEXT}</span>
      </label>
      {err && (
        <p className="m-0 text-[14px] text-down" role="alert">
          {err}
        </p>
      )}
      {!declining ? (
        <div className="flex flex-wrap gap-3">
          <PillButton disabled={!canSign} busy={busy} onClick={() => void sign()} className="flex-1">
            Sign
          </PillButton>
          <PillButton kind="secondary" disabled={staff || busy} onClick={() => setDeclining(true)}>
            Decline…
          </PillButton>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <label htmlFor="doc-decline" className="text-[14px] font-semibold">
            Why are you declining?
          </label>
          <textarea
            id="doc-decline"
            value={reason}
            maxLength={DOCUMENT_LIMITS.declineReasonMax}
            rows={3}
            autoFocus
            onChange={(e) => setReason(e.target.value)}
            className="rounded-[16px] border border-line bg-card px-4 py-3 text-[15px] text-fg outline-none focus:border-line-strong"
          />
          <div className="flex flex-wrap gap-3">
            <PillButton kind="destructive" disabled={!reason.trim()} busy={busy} onClick={() => void decline()} className="flex-1">
              Decline document
            </PillButton>
            <PillButton kind="secondary" disabled={busy} onClick={() => setDeclining(false)}>
              Cancel
            </PillButton>
          </div>
        </div>
      )}
    </Card>
  );
}

/** The panel beside the paper once the document can't be signed (or needs no signature). */
function StatusPanel({ doc, justSigned }: { doc: ClientDocument; justSigned: boolean }) {
  let text: ReactNode;
  if (doc.status === 'signed') text = justSigned ? 'Signed. A copy is in your Documents.' : `You signed this on ${fmtDate(doc.signedAt)}.`;
  else if (doc.status === 'declined') text = `You declined this on ${fmtDate(doc.declinedAt)}.`;
  else if (doc.status === 'expired' || doc.status === 'voided') text = 'This document is no longer available to sign. Contact your account manager.';
  else text = 'No signature needed. You can keep a copy.';
  const ok = doc.status === 'signed';
  return (
    <Card className="flex flex-col gap-5">
      <div className="flex items-start gap-3">
        <span className={cx('grid size-10 shrink-0 place-items-center rounded-full', ok ? 'bg-up-bg text-up' : 'bg-track text-muted')}>
          <AIcon name={ok ? 'check' : doc.status === 'declined' ? 'x' : 'info'} size={20} />
        </span>
        <p className="m-0 pt-2 text-[16px]">{text}</p>
      </div>
      {doc.status !== 'voided' && <DownloadButton doc={doc} kind={ok ? 'primary' : 'secondary'} />}
    </Card>
  );
}

export function DocumentPage({ id }: { id: string }) {
  const { data: doc, error, setData } = useLoad<ClientDocument>(`/api/documents/${id}`);
  const [justSigned, setJustSigned] = useState(false);
  // The first open marks it read: the badge and the list follow.
  useEffect(() => {
    if (doc) void loadDocuments();
  }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const back = (
    <Link to="/account/documents" className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-muted no-underline hover:text-fg">
      <Icon name="left" size={16} /> Documents
    </Link>
  );
  if (error && !doc)
    return (
      <div className="flex flex-col gap-6">
        {back}
        <Card>
          <p className="m-0 text-[16px]">{error === 'Document not found' ? 'This document isn’t in your account.' : error}</p>
        </Card>
      </div>
    );
  if (!doc)
    return (
      <div className="grid place-items-center py-16 text-muted">
        <Spinner />
      </div>
    );

  const signable = documentSignable(doc);
  return (
    <div className="flex flex-col gap-6">
      {back}
      {doc.message && doc.status !== 'voided' && (
        <Card className="!p-5 md:!px-8 md:!py-6">
          <div className="text-[13px] font-semibold text-muted">Message from {doc.agentName}</div>
          <p className="m-0 mt-1.5 text-[16px] leading-relaxed whitespace-pre-line">{doc.message}</p>
        </Card>
      )}
      {doc.status === 'voided' ? (
        <div className="max-w-[640px]">
          <StatusPanel doc={doc} justSigned={false} />
        </div>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,400px)] xl:gap-10">
          <Paper doc={doc} />
          <div className="flex flex-col gap-4 lg:sticky lg:top-6">
            {signable ? (
              <SignPanel
                doc={doc}
                onDone={(d) => {
                  setJustSigned(d.status === 'signed');
                  setData(d);
                }}
              />
            ) : (
              <StatusPanel doc={doc} justSigned={justSigned} />
            )}
            {signable && <DownloadButton doc={doc} />}
          </div>
        </div>
      )}
    </div>
  );
}
